import type {
  InfiniteData,
  QueryClient,
  QueryKey,
} from "@tanstack/react-query";
import type { ZodType } from "zod";
import type { AnySQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import { eq, getTableColumns } from "drizzle-orm";
import { queryCache } from "@sqlite/schema";
import type { MobileDatabase } from "../../runtime/ports";
import { describeError } from "./describeError";
import { createQueryCacheWrite } from "./queryCacheWrite";
import { stableStringify } from "./stableStringify";

/**
 * Global durable-cache persister for react-query infinite queries.
 *
 * react-query manages page-state internally for `useInfiniteQuery`, so we
 * don't have a single mutation site to wrap with `withDurableCache`. Instead
 * we hook into `QueryCache.subscribe` and mirror successful `state.data`
 * (which is `InfiniteData<T>` for an infinite query) to SQLite for any
 * query that opts in via `meta.durable === true`.
 *
 * Opt-in via the `durableInfiniteOptions` helper:
 *   const query = trpc.X.Y.useInfiniteQuery(input, {
 *     ...durableInfiniteOptions(queryKey, schema),
 *     getNextPageParam, initialPageParam, ...
 *   });
 * which composes `meta.durable` + initialData (with real fetchedAt for
 * staleTime) + initialDataUpdatedAt into the options shape react-query
 * expects.
 *
 * The handler is best-effort and silent on failure — never throws into the
 * react-query lifecycle. Optimistic updates via `setInfiniteData` flow
 * through this same path automatically (a `setQueryData` call also fires
 * the subscriber), so e.g. `useNotInterested.removeItem` survives a
 * kill-and-relaunch without any per-mutation write-through.
 *
 * One-way structural sharing: when a hook also sets `meta.decompose +
 * decomposeTable + decomposeIdField`, after writing the InfiniteData blob
 * to `query_cache` the persister also upserts each decomposed entity into
 * the per-entity table (e.g. `cached_artists`). This pre-warms the
 * by-id caches from feed responses — a subsequent `useArtists([id])` for
 * any artist the user just saw in the feed resolves from cache rather
 * than refetching.
 *
 * Returns a dispose function that detaches the subscriber.
 */
export function createInfiniteQueryCache({
  db,
  isCurrent,
  canWrite = () => isCurrent(),
  sqlite = { queryCache, eq, getTableColumns },
}: {
  db: MobileDatabase;
  isCurrent: () => boolean;
  canWrite?: (key: readonly unknown[]) => boolean;
  sqlite?: {
    queryCache: typeof queryCache;
    eq: typeof eq;
    getTableColumns: typeof getTableColumns;
  };
}) {
  const { queryCache, eq, getTableColumns } = sqlite;
  const queryCacheWrite = createQueryCacheWrite(db, sqlite);
  function setupDurableInfiniteQueryPersister(
    queryClient: QueryClient,
  ): () => void {
    return queryClient.getQueryCache().subscribe((event) => {
      if (!isCurrent() || event.type !== "updated") return;
      const query = event.query;
      if (query.state.status !== "success") return;
      if (query.meta?.durable !== true) return;
      // Guards the uid-change reset path (`setInfiniteData(input, undefined)`)
      // — without this we'd persist the empty post-reset state and overwrite
      // the previous user's good cache row.
      if (query.state.data === undefined) return;

      const key = stableStringify(query.queryKey);
      const schema = query.meta?.durableSchema as ZodType<unknown> | undefined;
      if (schema) {
        const parsed = schema.safeParse(query.state.data);
        if (!parsed.success) {
          console.warn(
            "[durableCache] infinite-query persister: schema parse failed, skipping write",
            { queryKey: key, error: parsed.error.message },
          );
          return;
        }
      }

      try {
        const result = queryCacheWrite(query.queryKey, query.state.data);
        // Drizzle's mutation builders may return a thenable; swallow async
        // failures so they don't surface as unhandled rejections.
        Promise.resolve()
          .then(() => {
            if (result && canWrite(query.queryKey)) result.run();
          })
          .catch((err) =>
            console.warn(
              "[durableCache] infinite-query persister: async write failed",
              { queryKey: key, error: describeError(err) },
            ),
          );
      } catch (err) {
        console.warn(
          "[durableCache] infinite-query persister: sync write threw",
          { queryKey: key, error: describeError(err) },
        );
      }

      // Optional structural-sharing pass: decompose pages → per-entity rows.
      const decompose = query.meta?.decompose as
        | ((data: InfiniteData<unknown>) => unknown[])
        | undefined;
      const decomposeTable = query.meta?.decomposeTable as
        | SQLiteTable
        | undefined;
      const decomposeIdField = query.meta?.decomposeIdField as
        | string
        | undefined;
      if (!decompose || !decomposeTable || !decomposeIdField) return;

      const decomposeSchema = query.meta?.decomposeSchema as
        | ZodType<unknown>
        | undefined;

      let entities: unknown[] = [];
      try {
        entities = decompose(query.state.data as InfiniteData<unknown>) ?? [];
      } catch (decomposeErr) {
        console.warn(
          "[durableCache] infinite-query persister: decompose callback threw",
          { queryKey: key, error: describeError(decomposeErr) },
        );
        return;
      }
      if (entities.length === 0) return;

      // Per-entity schema validation. Skip individual entities that fail
      // parsing (so one bad page item doesn't lose the whole feed's worth).
      const validated: unknown[] = [];
      for (const entity of entities) {
        if (decomposeSchema) {
          const parsed = decomposeSchema.safeParse(entity);
          if (!parsed.success) {
            console.warn(
              "[durableCache] infinite-query persister: decompose entity failed schema parse, skipping",
              { queryKey: key, error: parsed.error.message },
            );
            continue;
          }
          validated.push(parsed.data);
        } else {
          validated.push(entity);
        }
      }
      if (validated.length === 0) return;

      // Resolve column-object → JS-property-name mapping for the per-entity
      // table. We need the JS property names because `db.insert(t).values({…})`
      // keys by JS property name, not SQL column name.
      let idKey = "";
      let dataKey = "";
      let fetchedAtKey = "";
      let pkColumn: AnySQLiteColumn | undefined;
      try {
        const columns = getTableColumns(decomposeTable) as Record<
          string,
          AnySQLiteColumn
        >;
        for (const [jsKey, col] of Object.entries(columns)) {
          const sqlName = (col as { name?: string }).name;
          if (sqlName === "data") dataKey = jsKey;
          else if (sqlName === "fetched_at") fetchedAtKey = jsKey;
          else if ((col as { primary?: boolean }).primary) {
            pkColumn = col;
            idKey = jsKey;
          }
        }
      } catch (resolveErr) {
        console.warn(
          "[durableCache] infinite-query persister: failed to resolve decompose table columns",
          { queryKey: key, error: describeError(resolveErr) },
        );
        return;
      }
      if (!idKey || !dataKey || !fetchedAtKey || !pkColumn) {
        console.warn(
          "[durableCache] infinite-query persister: decompose table missing expected columns (id PK / data / fetched_at)",
          { queryKey: key },
        );
        return;
      }

      const writeNow = new Date();
      try {
        db.transaction((tx) => {
          for (const entity of validated) {
            const id = (entity as Record<string, unknown>)[decomposeIdField];
            if (typeof id !== "string" || id.length === 0) {
              console.warn(
                "[durableCache] infinite-query persister: decompose entity missing string id, skipping",
                { queryKey: key, idField: decomposeIdField },
              );
              continue;
            }
            const valuesRow: Record<string, unknown> = {
              [idKey]: id,
              [dataKey]: entity,
              [fetchedAtKey]: writeNow,
            };
            const setRow: Record<string, unknown> = {
              [dataKey]: entity,
              [fetchedAtKey]: writeNow,
            };
            tx.insert(decomposeTable)
              .values(valuesRow as never)
              .onConflictDoUpdate({
                target: pkColumn as AnySQLiteColumn,
                set: setRow as never,
              })
              .run();
          }
        });
      } catch (txErr) {
        console.warn(
          "[durableCache] infinite-query persister: decompose transaction threw",
          { queryKey: key, error: describeError(txErr) },
        );
      }
    });
  }

  /**
   * Synchronous read of a cached infinite-query row.
   *
   * Returns `undefined` if no row or the schema fails to parse. On schema
   * failure the row is purged so a new fetch re-populates with the current
   * shape (matches `readQueryCacheSync`'s upgrade-survival contract).
   *
   * Type parameter `T` is the return shape the caller wants — typically
   * `RouterOutputs[...]`, the strict page type react-query's `initialData`
   * expects. The schema is accepted as a `ZodType<InfiniteData<unknown>>`
   * because page schemas often use `.passthrough()` to allow server-internal
   * fields beyond the strict router shape (the inferred type then has an
   * `[k: string]: unknown` index signature that's NOT assignable to `T`).
   * Each queryFunctions call site asserts `RouterOutput extends z.infer<schema>`
   * (one-way for passthrough schemas, bidirectional otherwise) so the runtime
   * payload is guaranteed to satisfy `T` even though the compile-time schema
   * inference is wider.
   */
  function readInfiniteQueryCache<T>(
    queryKey: QueryKey,
    schema: ZodType<InfiniteData<unknown>>,
  ): InfiniteData<T> | undefined {
    return readInfiniteQueryCacheRow<T>(queryKey, schema)?.data;
  }

  /**
   * Like `readInfiniteQueryCache` but exposes the row's `fetchedAt`
   * timestamp. Used by `durableInfiniteOptions` to set `initialDataUpdatedAt`,
   * so per-query staleTime is respected on cold start.
   */
  function readInfiniteQueryCacheRow<T>(
    queryKey: QueryKey,
    schema: ZodType<InfiniteData<unknown>>,
  ): { data: InfiniteData<T>; fetchedAt: Date } | undefined {
    if (!isCurrent()) return undefined;
    const key = stableStringify(queryKey);
    try {
      const rows = db
        .select()
        .from(queryCache)
        .where(eq(queryCache.queryKey, key))
        .all();
      if (!rows[0]) return undefined;

      const parsed = schema.safeParse(rows[0].data);
      if (parsed.success) {
        return {
          data: parsed.data as unknown as InfiniteData<T>,
          fetchedAt: rows[0].fetchedAt,
        };
      }

      console.warn(
        "[durableCache] infinite-query cache row failed schema validation, purging",
        { queryKey: key, error: parsed.error.message },
      );
      db.delete(queryCache).where(eq(queryCache.queryKey, key)).run();
      return undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Compose the `meta + initialData + initialDataUpdatedAt` triple to spread
   * into a `useInfiniteQuery` options object. Replaces the 3-line opt-in
   * pattern with one call.
   *
   * Usage (inside a hook):
   *   const query = trpc.catalog.listForYouPaginated.useInfiniteQuery(input, {
   *     ...durableInfiniteOptions(queryKey, forYouInfiniteSchema),
   *     getNextPageParam: ...,
   *     initialPageParam: undefined,
   *     // other useInfiniteQuery-specific options
   *   });
   *
   * For structural-sharing (decompose into per-entity tables), pass extras:
   *   ...durableInfiniteOptions(queryKey, schema, {
   *     decompose: (data) => data.pages.flatMap(p => p.data),
   *     decomposeTable: cachedArtists,
   *     decomposeIdField: "id",
   *     decomposeSchema: artistSchema,
   *   })
   */
  function durableInfiniteOptions<T>(
    queryKey: QueryKey,
    schema: ZodType<InfiniteData<unknown>>,
    decomposeMeta?: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      decompose: (data: InfiniteData<any>) => unknown[];
      decomposeTable: SQLiteTable;
      decomposeIdField: string;
      decomposeSchema: ZodType<unknown>;
    },
  ) {
    return {
      meta: {
        durable: true,
        durableSchema: schema,
        ...(decomposeMeta ?? {}),
      },
      initialData: () => readInfiniteQueryCacheRow<T>(queryKey, schema)?.data,
      initialDataUpdatedAt: () =>
        readInfiniteQueryCacheRow<T>(queryKey, schema)?.fetchedAt?.getTime() ??
        0,
    };
  }

  return {
    setupDurableInfiniteQueryPersister,
    readInfiniteQueryCache,
    readInfiniteQueryCacheRow,
    durableInfiniteOptions,
  };
}
